/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap, Power1 } from 'gsap';
import {
    BackSide,
    BoxGeometry,
    type BufferGeometry,
    type CubeTexture,
    FrontSide,
    Group,
    LinearFilter,
    MathUtils,
    Mesh,
    Object3D,
    PerspectiveCamera,
    Raycaster,
    RepeatWrapping,
    Scene,
    ShaderMaterial,
    type Texture,
    UnsignedByteType,
    Vector2,
    Vector3,
    WebGLRenderTarget,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { type GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

import { listen, publish, publishBehaviorSubject } from '@pkg/components/Sections/Profile/pubSub';
import {
    glClearFaces,
    glDragDirection,
    glFaceChange,
    glLiquidEditMode,
    glLiquidIDDraggingToFaceIndex,
    glLiquidIdExpandedPreview,
    glLiquidIDOrbitControls,
    glLiquidIDRotate,
    glPauseAnimation,
    glShowFace,
    glShowUIButtons,
    glTouchEndTopic,
    glTouchLeaveTopic,
    glTouchMoveTopic,
    glTouchStartTopic,
} from '@pkg/components/Sections/Profile/pubSub/constants';
import { glLiquidIDFaceHoveredSubject, glUpdateFacesSubject } from '@pkg/components/Sections/Profile/pubSub/subjects';

import { type TokenV2Metadata } from '@app/client';
import { type NFTItem } from '@app/store/types/nft-manager';

import EventListeners, { type EventData } from '../../globals/EventListeners';
import Renderer from '../../globals/Renderer';
import { FACE_NAMES, FaceName, LID_LAYER_ID } from '../../shared';
import { asyncLoader } from '../../utils';
import Loaders from '../../utils/Loaders';
import LQBackground from '../LQBackground';
import LQParticles from '../LQParticles';

import AssetsManager from './AssetsManager';
import Material from './Material';
import Rotator from './Rotator';
import facesFragmentShader from './shaders/faces/fragmentShader';
import facesVertexShader from './shaders/faces/vertexShader';
import normalFragmentShader from './shaders/normal/fragmentShader';
import normalVertexShader from './shaders/normal/vertexShader';

export interface NFTTexture {
    faceName: FaceName;
    asset?: Texture;
    id?: string;
}

/**
 * YatCube is a 3D cube that can be used to display NFTs.
 */
export default class YatCube extends Object3D {
    private renderer: Renderer;
    // private camera: Camera;
    // private scene: Scene;
    private backFaceScene: TScene;
    private cubeRippleValue = 0;
    private faceRippleValue = 0;
    private currentFaceID = -1;
    // Used to keep track of loaded textures, keys are nft id
    private loadedNFTTextures: Record<string, Texture>;
    // Used to manage the order of textures across faces
    private texturesOfFaces: Array<NFTTexture>;
    private _liquidMultiplier = 0.75;

    backNormalsFBO!: WebGLRenderTarget;
    backMesh!: Mesh<BufferGeometry, ShaderMaterial>;
    glowMesh!: Mesh<BufferGeometry, ShaderMaterial>;
    frontMesh!: Mesh<BufferGeometry, ShaderMaterial>;
    innerMesh!: Mesh<BufferGeometry, ShaderMaterial>;
    frontGroup!: Group;
    faces: Mesh[] = [];
    assetsLoaded?: boolean;
    assetsManager: AssetsManager;
    hasVideoTexture: boolean;
    lidID: string;

    // Represented by euler angles
    private _angles = {
        [FaceName.f2]: new Vector3(0, 30, 0),
        [FaceName.f6]: new Vector3(0, -60, 0),
        [FaceName.f5]: new Vector3(180, -30, 180),
        [FaceName.f3]: new Vector3(-180, 60, -180),
        [FaceName.f4]: new Vector3(90, 360, -30),
        [FaceName.f1]: new Vector3(90, 360, 150),
    };

    private _currentFace = FaceName.f3;
    private _currentAnimation?: GSAPTween;
    private _targetAngle!: Vector3;

    private _rootBack = new Object3D();
    private _rootFront = new Object3D();
    private _rotator: Rotator;
    private _activeFaceMaterials: ShaderMaterial[] = [];
    private _currentFaceHovered = '';
    private _isEditorMode = false;
    private _isPublishMode = false;

    private _raycastOnContinuousRotation = true;
    private _raycaster = new Raycaster();

    private _wobbleAmplitude = 1;
    private _sineTime = 0; // Used to control the wobble of the cube
    private _draggingIndex?: number;

    constructor(world, id) {
        super();
        this.world = world;
        this.lidID = id;
    }

    async init() {
        const [yatLogoGLTF, cubeGLTF, envMap, noiseTexture, liquidNormalTexture, particleTexture] =
            await this.loadAssets();

        this.renderer = Renderer.getInstance();
        this.baseCameraPosition = new Vector3(0, 1.6, 8);
        this.camera = new PerspectiveCamera(30, 1 / 1, 0.2, 1000);
        this.camera.position.copy(this.baseCameraPosition);
        this.cameraTween = {
            currentAngle: 0,
            targetAngle: 0,
        };
        this.autoRotate = true;
        this.isEditMode = false;

        this.frameCheck = 0.0;

        // this.camera.lookAt(0, 0, 0);
        this.scene = new Scene();
        this.backFaceScene = new Scene();

        this.scene.add(this.camera);

        this.assetsManager = new AssetsManager();

        this.backNormalsFBO = new WebGLRenderTarget(1, 1, {
            type: UnsignedByteType,
            minFilter: LinearFilter,
            magFilter: LinearFilter,
        });

        this.frontGroup = new Group();
        this.frontGroup.renderOrder = -20;
        this.frontGroup.scale.setScalar(2);
        this._rootFront.add(this.frontGroup);
        this.add(this._rootFront);
        this.scene.add(this);
        this.texturesOfFaces = [];

        this.addObjects(yatLogoGLTF, cubeGLTF, envMap, noiseTexture, liquidNormalTexture);
        this.initParticles(particleTexture);
        this.initBackground(noiseTexture);

        this.initControls();
        this.eventListeners = new EventListeners(this);

        this._rotator = new Rotator({
            rootBack: this._rootBack,
            rootFront: this._rootFront,
            frontGroup: this.frontGroup,
            glowMesh: this.glowMesh,
            backMesh: this.backMesh,
        });

        this._targetAngle = this._angles[this._currentFace];

        window.__yatCube = this;
    }

    async loadAssets() {
        const [cubeGLTF, yatLogoGLTF, noiseTexture, liquidNormalTexture, envMap, particleTexture] = await Promise.all([
            asyncLoader('/assets/webgl/gltf/liquidid_2.glb', Loaders.gltfLoader, {
                dracoLoader: Loaders.dracoLoader,
            }) as unknown as GLTF,

            asyncLoader('/assets/webgl/gltf/yat_logo.glb', Loaders.gltfLoader, {
                dracoLoader: Loaders.dracoLoader,
            }) as unknown as GLTF,

            asyncLoader('/assets/webgl/textures/simplex-noise.jpg', Loaders.textureLoader) as unknown as Texture,

            asyncLoader(
                '/assets/webgl/textures/liquid-normal-smooth.jpeg',
                Loaders.textureLoader,
            ) as unknown as Texture,

            asyncLoader('/assets/webgl/textures/refractionSpec/', Loaders.textureCubeLoader, {
                files: ['px.png', 'nx.png', 'py.png', 'ny.png', 'pz.png', 'nz.png'],
            }) as unknown as CubeTexture,

            asyncLoader('/assets/webgl/textures/particles-single.png', Loaders.textureLoader) as unknown as Texture,
        ]);

        noiseTexture.wrapS = RepeatWrapping;
        noiseTexture.wrapT = RepeatWrapping;

        liquidNormalTexture.wrapS = RepeatWrapping;
        liquidNormalTexture.wrapT = RepeatWrapping;

        return [yatLogoGLTF, cubeGLTF, envMap, noiseTexture, liquidNormalTexture, particleTexture];
    }

    initControls(domElement) {
        this.controls = new OrbitControls(this.camera, domElement || this.renderer.domElement);
        this.controls.enabled = true;
        this.controls.enableDamping = true;
        this.controls.enableRotate = true;
        this.controls.dampingFactor = 0.08;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.autoRotate = this.autoRotate;
        this.controls.autoRotateSpeed = 1;
        this.controls.update();
    }

    setDomElement(domElement) {
        if (this.controls) {
            this.controls.dispose();
            delete this.controls;
        }
        this.eventListeners.removeListeners();

        if (!domElement) {
            this.leave();
            return;
        }

        this.initControls(domElement);
        this.transitionIn(true, true);
        this.eventListeners.createListeners(domElement);
    }

    setSize(width, height) {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.frontMesh.material.uniforms.resolution.value.x = width;
        this.frontMesh.material.uniforms.resolution.value.y = height;
        this.backNormalsFBO.setSize(width, height);
    }

    toggleCameraOrbit(value: boolean) {
        this._canOrbit = value;
        if (this.controls && this.yatCube) {
            this.controls.enabled = value;
        }
    }

    toggleOrbitRotating(value: boolean) {
        // Set this.autorotate in case controls is not created yet
        this.autoRotate = value;
        if (this.controls) {
            this.controls.autoRotate = this.autoRotate;
        }
    }

    initParticles(particleTexture) {
        this.lqParticles = new LQParticles(this.camera);
        this.lqParticles.position.y = -0.1;
        this.lqParticles.renderOrder = 10;
        this.lqParticles.particleTexture = particleTexture;

        this.add(this.lqParticles);
    }

    initBackground(noiseTexture) {
        this.lqBackground = new LQBackground();
        this.lqBackground.noiseTexture = noiseTexture;

        this.camera.add(this.lqBackground);
    }

    setBackgroundVisibility(isVisible) {
        this.lqBackground.visible = isVisible;
    }

    addListeners() {
        // Updates faces when FE alters content of Liquid ID
        glUpdateFacesSubject.subscribe(async ({ lidID, items }) => {
            if (lidID === this.lidID) await this.updateFaces(items);
        });

        if (this.lidID !== LID_LAYER_ID) return;

        // Events use for Main Liquid ID

        // Animate to face when FE publishes a face change
        listen(glShowFace, (faceName) => {
            if (this.controls && this.controls.enabled) {
                if (this.camera.position.distanceTo(this.baseCameraPosition) > 1) {
                    // Calculate target points from 2D coordinates

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore-disable-next-line
                    gsap.killTweensOf(this.cameraTween.currentAngle);
                    gsap.killTweensOf(this.camera.position);

                    this.cameraTween.currentAngle = this.getAngle(this.camera.position.x, this.camera.position.z);
                    this.cameraTween.targetAngle = this.getAngle(this.baseCameraPosition.x, this.baseCameraPosition.z);

                    // gsap.killTweensOf(this.camera.position);

                    gsap.to(this.cameraTween, {
                        currentAngle: this.cameraTween.targetAngle,
                        duration: 0.4,
                        ease: 'power3.out',
                        onUpdate: () => {
                            const currentPosition = this.getCirclePoint(
                                this.cameraTween.currentAngle,
                                this.baseCameraPosition.z,
                            );
                            this.camera.position.x = currentPosition.x;
                            this.camera.position.z = currentPosition.y;
                        },
                    });

                    gsap.to(this.camera.position, {
                        y: 1.6,
                        duration: 0.4,
                        ease: 'power3.out',
                    });
                }
            }

            this.faceChanged(faceName);
        });

        // On hover over canvas, find the NFT details of the currently hovered face
        listen(glTouchMoveTopic, (ev: EventData) => {
            if (this.controls && this._canOrbit) {
                this.controls.saveState();
            }
            if (!this._isEditorMode && this._isPublishMode) {
                this._runRayPicker(ev);
            }
        });

        listen(glClearFaces, () => this.clearUniforms());

        listen(glPauseAnimation, () => {
            if (this._currentAnimation) {
                this._currentAnimation.kill();
            }
        });

        // Used to help animate to a face when the user is dragging thumbnails
        listen(glLiquidIDDraggingToFaceIndex, (value: number | undefined) => {
            this._draggingIndex = value;
        });

        // LiquidIDExpandedPreview is the viewing only mode of Liquid ID
        listen(glLiquidIdExpandedPreview, (value: boolean) => {
            this._isPublishMode = value;
            this._raycastOnContinuousRotation = value;
        });

        // If user interacting, toggle animations/raycasting
        listen(glTouchStartTopic, () => this._toggleOnMouseInteraction(false));
        listen(glTouchEndTopic, () => this._toggleOnMouseInteraction(true));
        listen(glTouchLeaveTopic, () => () => {
            this._toggleOnMouseInteraction(true);
            if (this.controls && this._canOrbit) {
                this.controls.saveState();
                this.controls.enabled = false;
                this.controls.reset();
                this.controls.enabled = true;
            }
        });

        // Toggle wobble motion if liquid id is being edited
        listen(glLiquidEditMode, (value: boolean) => {
            this.wobble = !value;
            this.isEditMode = value;
        });

        listen(glLiquidIDOrbitControls, this.toggleCameraOrbit.bind(this));

        listen(glLiquidIDRotate, this.toggleOrbitRotating.bind(this));

        listen(glDragDirection, (value: number) => {
            if (this.controls) {
                this.controls.autoRotateSpeed = 1 * value;
            }
        });
    }

    /**
     * Toggles raycasting on continuous rotation
     * @param value - toggle raycasting on/off
     */
    toggleRaycastContinuousRotation(value: boolean) {
        if (!this._isEditorMode) return;
        if (value) {
            setTimeout(() => {
                this._raycastOnContinuousRotation = value;
            }, 1000);
        } else {
            this._raycastOnContinuousRotation = value;
        }
    }

    // Get angle of 2D points from center
    getAngle(pointX, pointY, centerX = 0, centerY = 0) {
        const x = pointX - centerX;
        const y = pointY - centerY;

        return Math.atan2(y, x);
    }

    // Get 2D points from angle and radius
    getCirclePoint(angle, radius) {
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        return {
            x,
            y,
        };
    }

    /**
     * Toggles animations and raycasting on/off based on mouse interaction
     * @param value - true to turn on, false to turn off
     */
    private _toggleOnMouseInteraction(value: boolean) {
        if (this._isPublishMode) {
            this.wobble = value;
        }
        this.toggleRaycastContinuousRotation(value);
    }

    private _transitionOut() {
        publish(glShowUIButtons, false);

        this.lqParticles.transitionOut();

        this.faces.forEach((face) => {
            const faceMaterial = face.material as ShaderMaterial;
            gsap.to(faceMaterial.uniforms.opacity, {
                value: 0,
                duration: 0.5,
            });
        });

        gsap.to([this.frontMesh.material.uniforms.opacity, this.glowMesh.material.uniforms.opacity], {
            value: 0,
            duration: 0.5,
            onStart: () => {
                setTimeout(() => {
                    this._rotator.resetRootRotations();
                    this._rotator.rotateToTarget(this._angles[FaceName.f6]);
                }, 500);
            },
            onComplete: () => {
                this._rootFront.visible = false;
                this._rootBack.visible = false;
            },
        });

        this._isEditorMode = false;
    }

    transitionIn(forceShow = true) {
        const isExpanded = this._isPublishMode || this.isEditMode;

        if (this._isPublishMode || this.isEditMode) this.lqParticles.transitionIn();
        else this.lqParticles.hide();

        this.lqBackground.transitionIn(true);

        this.camera.position.copy(this.baseCameraPosition);
        this.camera.lookAt(this.frontGroup.position);

        this.lqBackground.position.z = -12;

        // Make sure face is hide during new page transition
        this.faces.forEach((mesh) => {
            if (mesh.material) {
                mesh.material.uniforms.opacity.value = 0;
                mesh.material.uniforms.transitionValue.value = 0;
            }
        });

        // Hide Liquid ID Thumbnails
        publish(glShowUIButtons, isExpanded);

        if (isExpanded && !this._isPublishMode) {
            this._isEditorMode = true;
        } else {
            this._isEditorMode = false;
        }

        gsap.killTweensOf([this.frontMesh.material.uniforms.opacity, this.glowMesh.material.uniforms.opacity]);

        if (forceShow) {
            // Fade in cube material
            gsap.fromTo(
                [this.frontMesh.material.uniforms.opacity, this.glowMesh.material.uniforms.opacity],
                {
                    value: 0,
                },
                {
                    value: 1,
                    duration: 2,
                    delay: 0.3,
                    onStart: () => {
                        this._rootFront.visible = true;
                        this._rootBack.visible = true;
                    },
                    onComplete: () => {
                        publish(glShowUIButtons, true);
                    },
                },
            );
        } else {
            // Fade in cube material
            gsap.to([this.frontMesh.material.uniforms.opacity, this.glowMesh.material.uniforms.opacity], {
                value: 1,
                duration: 2,
                delay: 0.3,
                onStart: () => {
                    this._rootFront.visible = true;
                    this._rootBack.visible = true;
                },
                onComplete: () => {
                    publish(glShowUIButtons, true);
                },
            });
        }

        this._rotator.resetRootRotations();
        this._rotator.rotateToTarget(this._angles[FaceName.f6]);

        this.faces.forEach((face: Mesh) => {
            setTimeout(() => {
                this.transitionFace({ faceName: face.name }, 'in');
            }, 1000);
        });
    }

    set wobble(value: boolean) {
        gsap.set(this, {
            _wobbleAmplitude: value ? 1 : 0,
        });
    }

    /**
     * Finds the cube face hit by the raycaster
     * @param ev
     */
    private _runRayPicker(ev: EventData) {
        this._raycaster.setFromCamera(new Vector2(ev.normalized.x, ev.normalized.y), this.camera);
        const intersects = this._raycaster.intersectObjects(this.faces, false);
        for (let i = 0; i < intersects.length; i++) {
            const intersectedFaceName = intersects[i].object.name;
            if (this._currentFaceHovered != intersectedFaceName) {
                const currentFaceId = FACE_NAMES.indexOf(intersectedFaceName as FaceName);
                publishBehaviorSubject(glLiquidIDFaceHoveredSubject, currentFaceId);
                this._currentFaceHovered = intersectedFaceName;
            }
        }
    }

    private addObjects(
        yatLogoGLTF: GLTF,
        cubeGLTF: GLTF,
        envMap: Texture,
        noiseTexture: Texture,
        liquidNormal: Texture,
    ) {
        // add the inner mesh to the normal render scene
        const yatLogoMesh = yatLogoGLTF.scene.children[0] as Mesh;
        const yatLogoGeometry: BufferGeometry = yatLogoMesh.geometry;
        yatLogoGeometry.rotateX(MathUtils.degToRad(90));

        this.innerMesh = new Mesh(
            yatLogoGeometry,
            new ShaderMaterial({
                vertexShader: normalVertexShader,
                fragmentShader: normalFragmentShader,
            }),
        );
        this.innerMesh.scale.setScalar(0.8);
        this.innerMesh.renderOrder = 0;

        this._rootBack.add(this.innerMesh);
        this.backFaceScene.add(this._rootBack);

        const gltfScene = cubeGLTF.scene;

        this.traverseGLTF(gltfScene, envMap, noiseTexture, liquidNormal);
        this.addListeners();

        this.assetsLoaded = true;
    }

    /**
     * Handles transitions and updates class properties on reorder / remove changes
     * @param items - Array of items to be reordered
     * @param delay - Delay before updating shader uniforms
     */
    private async updateTextureOrder(items: NFTItem[], delay = 0) {
        const updatedTextureOrder: NFTTexture[] = [];

        FACE_NAMES.forEach((face) => {
            let hasAsset = false;
            items.forEach((item) => {
                if (item.faceName === face) {
                    updatedTextureOrder.push(item);
                    hasAsset = true;
                }
            });

            if (!hasAsset) {
                const faceMesh = this.faces.find((mesh: Mesh) => mesh.name === face);
                if (faceMesh) {
                    this.cleanFaceMaterial(faceMesh.material);
                }
            }
        });

        this.texturesOfFaces = updatedTextureOrder;

        this.updateUniforms(delay);
    }

    private clearUniforms() {
        this.faces.forEach((face: Mesh, index) => {
            const material = face.material as ShaderMaterial;
            material.uniforms.nftMap.value = undefined;
            material.uniforms.aspectRatio.value.x = 1.0;
            material.uniforms.aspectRatio.value.y = 1.0;
            this.transitionFace({ faceName: FACE_NAMES[index] }, 'out');
        });
    }

    private clearFaces() {
        this.faces.forEach((face: Mesh) => {
            const material = face.material as ShaderMaterial;
            material.uniforms.nftMap.value = undefined;
            material.uniforms.transitionValue.value = 0;
            material.uniforms.opacity.value = 0;
        });
    }
    /**
     *  Updates the uniforms of the shader materials
     * @param delay - delay in seconds
     * @param newItem - boolean to indicate if the item is new or not
     */
    private updateUniforms(delay = 0, newItem = false) {
        if (!this.assetsLoaded) return;

        this.faces.forEach((face: Mesh) => {
            const objectTexture = this.texturesOfFaces.find((f) => f.faceName === face.name, delay);

            if (objectTexture && objectTexture.asset) {
                const material = face.material as ShaderMaterial;

                const textureWidth = objectTexture.asset.image.videoWidth || objectTexture.asset.image.width || 0;
                const textureHeight = objectTexture.asset.image.videoHeight || objectTexture.asset.image.height || 0;

                let aspectRatioX = 1.0;
                let aspectRatioY = 1.0;

                if (textureWidth > 0) {
                    aspectRatioX = textureWidth < textureHeight ? 1.0 : textureHeight / textureWidth;
                    aspectRatioY = textureWidth < textureHeight ? textureWidth / textureHeight : 1.0;
                }

                material.uniforms.aspectRatio.value.x = aspectRatioX;
                material.uniforms.aspectRatio.value.y = aspectRatioY;

                material.uniforms.nftMap.value = objectTexture.asset;
            } else {
                const material = face.material as ShaderMaterial;
                if (material.uniforms.nftMap.value) {
                    material.uniforms.nftMap.value = null;
                }
            }
        });

        if (!newItem) {
            if (this.texturesOfFaces.length && this._draggingIndex !== undefined) {
                this.faceChanged(FACE_NAMES[this._draggingIndex]);
            }
        }
    }

    cleanFaceMaterial(material) {
        gsap.killTweensOf([material.uniforms.transitionValue, material.uniforms.opacity]);
        material.uniforms.opacity.value = 0;
        material.uniforms.transitionValue.value = 0;
    }

    updateFaceUniforms(texture, faceName) {
        this.faces.forEach((face: Mesh) => {
            if (face.name === faceName) {
                const material = face.material as ShaderMaterial;

                const textureWidth = texture.image.videoWidth || texture.image.width || 0;
                const textureHeight = texture.image.videoHeight || texture.image.height || 0;

                let aspectRatioX = 1.0;
                let aspectRatioY = 1.0;

                if (textureWidth > 0) {
                    aspectRatioX = textureWidth < textureHeight ? 1.0 : textureHeight / textureWidth;
                    aspectRatioY = textureWidth < textureHeight ? textureWidth / textureHeight : 1.0;
                }

                material.uniforms.aspectRatio.value.x = aspectRatioX;
                material.uniforms.aspectRatio.value.y = aspectRatioY;

                material.uniforms.nftMap.value = texture;
            }
        });
    }

    /**
     * Fade in/out a face
     */
    private transitionFace({ faceName }: { faceName: FaceName }, direction: string) {
        const faceMesh = this.faces.find((face: Mesh) => face.name === faceName);
        if (!faceMesh) return;

        const mat = faceMesh?.material as ShaderMaterial;
        mat.name = faceName;

        if (!mat.uniforms.nftMap.value) {
            mat.uniforms.transitionValue.value = 0.0;
            mat.uniforms.opacity.value = 0.0;

            return;
        }

        if (mat.uniforms.transitionValue.value > 0.0 && mat.uniforms.opacity.value > 0.0 && direction === 'in') return;

        if (mat.uniforms.transitionValue.value === 0.0 && mat.uniforms.opacity.value === 0.0 && direction === 'out')
            return;

        gsap.killTweensOf([mat.uniforms.transitionValue, mat.uniforms.opacity]);

        const start = direction === 'in' ? 0 : 1;
        const end = 1 - start;

        // keep track of active face material
        if (direction === 'in') {
            this._activeFaceMaterials.push(faceMesh.material as ShaderMaterial);
        } else {
            const index = this._activeFaceMaterials.map((mat) => mat.name).indexOf(faceName);
            this._activeFaceMaterials.splice(index);
        }

        gsap.fromTo(
            [mat.uniforms.transitionValue, mat.uniforms.opacity],
            {
                value: start,
            },
            {
                value: end,
                duration: direction === 'in' ? 1 : 0.6,
                ease: Power1.easeOut,
            },
        );
    }

    /**
     * Changes faces, triggering ripple and rotation
     * @param faceName Name of Face object
     */
    private faceChanged(faceName: FaceName): void {
        const index = FACE_NAMES.findIndex((f) => f === faceName);

        this._targetAngle = this._angles[faceName];
        this._rotator.rotateToTarget(this._targetAngle);

        if (this._currentAnimation) {
            this._currentAnimation.kill();
        }

        if (index != this.currentFaceID) {
            this.cubeRippleValue = 80;
            this.faceRippleValue = 5;
        }

        this._currentAnimation = gsap.to(this, {
            duration: 2.5,
            ease: Power1.easeOut,
            onInterrupt: () => {
                this._currentFace = faceName;
                this.currentFaceID = index;
            },
            onComplete: () => {
                this._currentFace = faceName;
                this.currentFaceID = index;
                publish(glFaceChange, index);
            },
        });
    }

    replaceFallbackImage(texture, faceName) {
        this.texturesOfFaces.forEach((textureObject) => {
            if (textureObject.faceName === faceName) {
                textureObject.asset = texture;
                textureObject.asset.canUpdate = false;
            }
        });
    }

    /**
     * Loads NFTs as textures to place on the faces
     **/
    private async updateFaces(items: TokenV2Metadata[]) {
        const updatedTextureOrder: NFTTexture[] = await this.assetsManager.checkTextures(items, this);

        if (!updatedTextureOrder) return;

        this.updateTextureOrder(updatedTextureOrder);
    }

    private traverseGLTF(group: Group, envMap: Texture, noiseTexture: Texture, liquidNormal: Texture) {
        const normalMaterial = new ShaderMaterial({
            vertexShader: normalVertexShader,
            fragmentShader: normalFragmentShader,
            uniforms: {
                time: { value: 0 },
                twist: { value: 0 },
            },
            side: BackSide,
        });

        const frontMaterial = new Material() as ShaderMaterial;
        frontMaterial.uniforms.envMap.value = envMap;
        frontMaterial.uniforms.backfaceNormalTexture.value = this.backNormalsFBO.texture;
        frontMaterial.uniforms.noiseTexture.value = noiseTexture;
        frontMaterial.uniforms.liquidNormalTexture.value = liquidNormal;
        frontMaterial.uniforms.resolution.value = new Vector2(1.0, 1.0);

        const glowMaterial = new Material() as ShaderMaterial;
        glowMaterial.uniforms.envMap.value = envMap;
        glowMaterial.uniforms.backfaceNormalTexture.value = this.backNormalsFBO.texture;
        glowMaterial.uniforms.noiseTexture.value = noiseTexture;
        glowMaterial.uniforms.liquidNormalTexture.value = liquidNormal;
        glowMaterial.uniforms.resolution.value = new Vector2(1.0, 1.0);
        this.glowMesh = new Mesh(new BoxGeometry(), glowMaterial);
        this.glowMesh.position.y = 0.0;
        this.glowMesh.scale.setScalar(2.2);

        let faceCount = 0;

        group.traverse((object: Object3D | Mesh) => {
            if (object instanceof Mesh) {
                if (object.name === 'base') {
                    // add back mesh to normal render scene
                    this.backMesh = new Mesh(object.geometry, normalMaterial);
                    this.backMesh.scale.setScalar(2);
                    this._rootBack.add(this.backMesh);

                    // add front mesh to front group
                    this.frontMesh = new Mesh(object.geometry, frontMaterial);
                    this.frontGroup.add(this.frontMesh);
                    this._rootFront.add(this.frontGroup);
                } else {
                    faceCount++;
                    this.generateFace(object, liquidNormal, faceCount);
                }
            }
        });
    }

    /**
     * Generates a face mesh for the cube
     * @param mesh Mesh to generate face from
     * @param liquidNormal Texture for the liquid normal
     * @param faceIndex index of face on cube
     */
    private generateFace(object: Mesh, liquidNoiseMap: Texture, faceIndex: number) {
        const face = object.clone();
        face.renderOrder = 3;
        face.material = new ShaderMaterial({
            vertexShader: facesVertexShader,
            fragmentShader: facesFragmentShader,
            transparent: true,
            uniforms: {
                time: { value: 0 },
                twist: { value: 0 },
                liquidNoiseMap: { value: liquidNoiseMap },
                transitionValue: { value: 0 },
                opacity: { value: 0 },
                nftMap: { value: null },
                distortion: { value: 0 },
                aspectRatio: { value: new Vector2(1, 1) },
                faceID: { value: faceIndex },
            },
            side: FrontSide,
        });
        // add nft faces to front group
        face.scale.setScalar(1.001);
        this.frontGroup.add(face);
        this.faces.push(face);
    }

    /**
     * Creates a sine wave
     */
    private sineOscillator(time: number, frequency = 1, amplitude = 1, phase = 0, offset = 0): number {
        return Math.sin(time * frequency * Math.PI * 2 + phase * Math.PI * 2) * amplitude + offset;
    }

    update(elapsed, delta: { elapsed: number; delta: number }) {
        if (!this.visible) return;
        if (!this.assetsLoaded) return;

        if (this.controls) this.controls.update();

        this.backMesh.material.uniforms.time.value = elapsed;
        this.frontMesh.material.uniforms.time.value = elapsed;

        this.cubeRippleValue += this._rotator.rootVelocity;
        this.cubeRippleValue *= 0.98;

        this.frontMesh.material.uniforms.rippleAmount.value =
            Math.min(2.0, Math.max(this.cubeRippleValue, 0.5)) * this._liquidMultiplier;

        this.faceRippleValue += this._rotator.rootVelocity;
        this.faceRippleValue *= 0.96;

        this._rotator.update({ elapsed, delta });

        if (this._isEditorMode !== this._rotator.expandedView) {
            this._rotator.expandedView = this._isEditorMode;
        }

        // Rotate continuously if not being edited
        if (!this._isEditorMode) {
            this._rotator.rotateContinuous(0.01 * delta);
        }

        // Wobble motion
        if (this._wobbleAmplitude > 0) {
            // control elapsed time for sine to prevent shifts when clicking/unclicking
            this._sineTime += delta;
        }

        // Get new sine value
        const sineValue = this.sineOscillator(this._sineTime, 0.25, 0.5, 0.5, 0.5);

        // Apply new sine value to y axis of groups
        this._rootFront.position.y = 0.01 + sineValue * 0.1;
        this._rootBack.position.y = 0.01 + sineValue * 0.1;

        this.faces.forEach((face) => {
            const mat = face.material as ShaderMaterial;
            mat.uniforms.distortion.value = Math.max(this.faceRippleValue, 0) * this._liquidMultiplier;
            mat.uniforms.time.value = elapsed;
        });

        // Run twice per second
        const floorElasped = Math.floor(elapsed * 2.0);
        if (floorElasped !== this.frameCheck) {
            if (
                this.assetsManager.hasVideoTexture ||
                (this._raycastOnContinuousRotation && (this._isEditorMode || this._isPublishMode))
            ) {
                this._runRayPicker({ normalized: { x: 0, y: 0 } } as EventData);
            }

            if (this.assetsManager.hasVideoTexture) this.checkVideoInFront();
        }

        this.frameCheck = floorElasped;
    }

    lateUpdate() {
        if (!this.visible) return;
        if (!this.assetsLoaded) return;

        this.renderer.setRenderTarget(this.backNormalsFBO);
        this.renderer.render(this.backFaceScene, this.camera);
        this.renderer.setRenderTarget(null);
    }

    checkVideoInFront() {
        for (let i = 0; i < this.texturesOfFaces.length; i++) {
            if (this.texturesOfFaces[i].asset) {
                if (this.texturesOfFaces[i].asset.isVideoTexture && this.texturesOfFaces[i].asset._isPlayable) {
                    if (this.texturesOfFaces[i].faceName === this._currentFaceHovered) {
                        if (!this.texturesOfFaces[i].asset.canUpdate) {
                            this.texturesOfFaces[i].asset.canUpdate = true;
                            this.texturesOfFaces[i].asset.source.data.play();
                        }
                    } else if (this.texturesOfFaces[i].asset.canUpdate) {
                        this.texturesOfFaces[i].asset.canUpdate = false;
                        this.texturesOfFaces[i].asset.source.data.pause();
                    }
                }
            }
        }
    }

    leave() {
        for (const key in this.loadedNFTTextures) {
            if (this.loadedNFTTextures[key].isVideoTexture) {
                this.loadedNFTTextures[key].source.data.pause();
            }
        }
    }

    delete() {
        delete this.controls;
        this.eventListeners.removeListeners();
        this.innerMesh.geometry.dispose();
        this.innerMesh.material.dispose();
        this.lqParticles.delete();
        this.glowMesh.geometry.dispose();
        this.glowMesh.material.dispose();
        this.backMesh.geometry.dispose();
        this.backMesh.material.dispose();
        this.frontMesh.geometry.dispose();
        this.frontMesh.material.dispose();
        this.backNormalsFBO.dispose();
        this.faces.forEach((face) => {
            face.geometry.dispose();
            face.material.dispose();
        });
        this.assetsManager.cleanTextures();
    }
}
